import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  useCreateAttendance,
  useUpdateAttendance,
  useDeleteAttendance,
} from '../../../services/httpServices/queries';
import type { Attendance, Leave, Shift } from '../../../services/httpServices/hrService';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { formatShiftTime, shiftColorStyle, to24h } from './utils';
import {
  CalendarClock,
  Check,
  Clock,
  Loader2,
  Moon,
  Trash2,
  Umbrella,
  X,
} from 'lucide-react';

export interface AssignModalData {
  employeeId: string;
  employeeName: string;
  date: string;
  existing?: Attendance;
  onLeave?: Leave;
  /** True when the date is one of the employee's paid weekly off days. */
  isWeeklyOff?: boolean;
}

interface AssignShiftModalProps {
  open: boolean;
  data: AssignModalData | null;
  shifts: Shift[];
  onClose: () => void;
}

export function AssignShiftModal({ open, data, shifts, onClose }: AssignShiftModalProps) {
  const createAttendance = useCreateAttendance();
  const updateAttendance = useUpdateAttendance();
  const deleteAttendance = useDeleteAttendance();

  const [shiftId, setShiftId] = useState('');
  const [notes, setNotes] = useState('');
  const [showRemove, setShowRemove] = useState(false);

  useEffect(() => {
    if (!open || !data) return;
    const existing = data.existing;
    const existingShift =
      shifts.find((s) => s.id === existing?.shiftId) ||
      shifts.find((s) => s.name === existing?.shiftType || s.shiftCode === existing?.shiftType);

    setShiftId(existingShift?.id || shifts[0]?.id || '');
    setNotes(existing?.notes || '');
    setShowRemove(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data?.employeeId, data?.date, data?.existing?.id, shifts.length]);

  if (!open || !data) return null;

  const selectedShift = shifts.find((s) => s.id === shiftId);

  const isPending = createAttendance.isPending || updateAttendance.isPending;
  const isExisting = !!data.existing;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!shiftId || !selectedShift) {
      toast.error('Please select a shift');
      return;
    }
    const payload = {
      employeeId: data.employeeId,
      date: data.date,
      status: 'PRESENT',
      shiftId,
      shiftType: selectedShift.shiftCode,
      checkIn: `${data.date}T${to24h(selectedShift.startTime)}:00`,
      checkOut: `${data.date}T${to24h(selectedShift.endTime)}:00`,
      notes,
    };

    const opts = {
      onSuccess: () => {
        toast.success(isExisting ? 'Shift updated' : 'Shift assigned');
        onClose();
      },
      onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to save shift'),
    };

    if (isExisting && data.existing) {
      updateAttendance.mutate({ id: data.existing.id, data: payload }, opts);
    } else {
      createAttendance.mutate(payload, opts);
    }
  };

  const handleRemove = () => {
    if (!data.existing) return;
    deleteAttendance.mutate(data.existing.id, {
      onSuccess: () => {
        toast.success('Shift removed');
        setShowRemove(false);
        onClose();
      },
      onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to remove shift'),
    });
  };

  const formattedDate = new Date(`${data.date}T00:00:00`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-4 py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow-sm">
              <CalendarClock size={17} />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold leading-tight text-text">
                {isExisting ? 'Update Shift' : 'Assign Shift'}
              </h2>
              <p className="mt-0.5 truncate text-xs text-text-muted">
                {data.employeeName} · {formattedDate}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text"
            aria-label="Close"
          >
            <X size={17} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[calc(90vh-3.5rem)] space-y-3 overflow-y-auto p-4">
          {/* Context */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-border/70 bg-background/60 px-3 py-2">
              <p className="text-[10px] font-medium uppercase tracking-wider text-text-muted">Employee</p>
              <p className="mt-0.5 truncate text-sm font-medium text-text">{data.employeeName}</p>
            </div>
            <div className="rounded-xl border border-border/70 bg-background/60 px-3 py-2">
              <p className="text-[10px] font-medium uppercase tracking-wider text-text-muted">Date</p>
              <p className="mt-0.5 truncate text-sm font-medium text-text">{formattedDate}</p>
            </div>
          </div>

          {data.onLeave && (
            <div className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger/10 p-2.5 text-danger">
              <Umbrella size={15} className="mt-0.5 shrink-0" />
              <div className="min-w-0 text-xs">
                <p className="font-semibold">Employee is on approved leave</p>
                <p className="mt-0.5 opacity-90">
                  {data.onLeave.type} Leave
                  {data.onLeave.reason ? `: ${data.onLeave.reason}` : ''}
                </p>
              </div>
            </div>
          )}

          {data.isWeeklyOff && !data.onLeave && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/10 p-2.5 text-amber-700">
              <Moon size={15} className="mt-0.5 shrink-0" />
              <div className="min-w-0 text-xs">
                <p className="font-semibold">Weekly off day</p>
                <p className="mt-0.5 opacity-90">
                  Assigning a shift here counts as overtime in payroll.
                </p>
              </div>
            </div>
          )}

          {shifts.length === 0 ? (
            <div className="rounded-xl border border-warning/20 bg-warning/10 p-3 text-xs text-warning">
              No shifts available. Create one in the <span className="font-semibold">Shifts</span> tab first.
            </div>
          ) : (
            <>
              {/* Shift picker */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-text">Shift</label>
                {shifts.length <= 4 ? (
                  <div className="grid gap-1.5">
                    {shifts.map((shift) => {
                      const active = shift.id === shiftId;
                      return (
                        <button
                          key={shift.id}
                          type="button"
                          onClick={() => setShiftId(shift.id)}
                          className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left transition-all ${
                            active
                              ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20'
                              : 'border-border hover:border-primary/30 hover:bg-background/60'
                          }`}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: shift.color || '#0f766e' }}
                            />
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-text">
                                {shift.name}
                              </span>
                              <span className="mt-0.5 flex items-center gap-1 text-[11px] text-text-muted">
                                <Clock size={10} />
                                {formatShiftTime(shift.startTime)} – {formatShiftTime(shift.endTime)}
                                {shift.breakMinutes ? ` · ${shift.breakMinutes}m break` : ''}
                              </span>
                            </span>
                          </span>
                          {active && (
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                              <Check size={12} />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <select
                    value={shiftId}
                    onChange={(e) => setShiftId(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-text outline-none transition focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="" disabled>
                      Select a shift
                    </option>
                    {shifts.map((shift) => (
                      <option key={shift.id} value={shift.id}>
                        {shift.name} ({formatShiftTime(shift.startTime)} – {formatShiftTime(shift.endTime)})
                      </option>
                    ))}
                  </select>
                )}

                {shifts.length > 4 && selectedShift && (
                  <div
                    className="mt-2 inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-medium"
                    style={shiftColorStyle(selectedShift.color)}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: selectedShift.color }}
                    />
                    {selectedShift.name}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-text">
                  Notes <span className="font-normal text-text-muted">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a note for this assignment"
                  rows={2}
                  className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm text-text outline-none transition placeholder:text-text-muted/70 focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
            {isExisting ? (
              <button
                type="button"
                onClick={() => setShowRemove(true)}
                className="flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-xs font-medium text-danger transition-colors hover:bg-danger/10"
              >
                <Trash2 size={14} />
                Remove
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-3 py-2 text-xs font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending || shifts.length === 0}
                className="flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-dark disabled:opacity-50"
              >
                {isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {isExisting ? 'Save Changes' : 'Assign Shift'}
              </button>
            </div>
          </div>
        </form>
      </div>

      <ConfirmDialog
        open={showRemove}
        title="Remove shift?"
        description="This clears the assigned shift for this day."
        confirmLabel="Remove"
        loading={deleteAttendance.isPending}
        onConfirm={handleRemove}
        onCancel={() => setShowRemove(false)}
      />
    </div>
  );
}
